import "./index.css"

import { InputTextBase } from "@components/input-text-base"
import classNames from "classnames"
import React, { forwardRef, type InputHTMLAttributes } from "react"

export type InputSize = "lg" | "md"

export type InputType =
  | "email"
  | "number"
  | "password"
  | "search"
  | "tel"
  | "text"
  | "url"

interface InputBaseProps {
  /** If true, the input width grows to fit content. */
  autoWidth?: boolean
  /** Helper text displayed below the input. Styled as an error when `invalid`. */
  description?: string
  /** Whether the input is in an invalid state. */
  invalid?: boolean
  /** If true, all text is selected when the input receives focus. */
  selectTextOnFocus?: boolean
  /** The size of the input. */
  size?: InputSize
  /** Supported input types — defaults to 'text' */
  type?: InputType
}

interface InputWithVisibleLabel extends InputBaseProps {
  /** If true, the label is visually hidden and set as aria-label. */
  hideLabel?: false
  /** The label text for the input. */
  label: React.ReactNode
}

interface InputWithHiddenLabel extends InputBaseProps {
  /** If true, the label is visually hidden and set as aria-label. */
  hideLabel: true
  /** The label text for the input. Must be a string when hidden. */
  label: string
}

interface InputWithAriaLabelledBy extends InputBaseProps {
  "aria-labelledby": string
  hideLabel?: never
  label?: never
}

export type InputProps =
  | InputWithAriaLabelledBy
  | InputWithHiddenLabel
  | InputWithVisibleLabel

/**
 * Combines standard HTML input attributes with custom Input props.
 * Omits 'size' and 'type' from HTML attributes to avoid conflict with our strict types.
 */
export type InputElementProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  keyof InputProps | "size" | "type"
> &
  InputProps

export const Input = forwardRef<HTMLInputElement, InputElementProps>(
  function Input(
    {
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      autoComplete,
      autoWidth,
      className,
      description,
      hideLabel,
      id,
      invalid,
      label,
      onFocus,
      selectTextOnFocus,
      size = "md",
      type = "text",
      ...restProps
    }: InputElementProps,
    ref
  ) {
    const autoCompleteBlockingProps =
      autoComplete === "off"
        ? {
            "data-1p-ignore": true,
            "data-bwignore": true,
            "data-form-type": "other",
            "data-lpignore": true,
          }
        : {}

    const handleFocus = selectTextOnFocus
      ? (event: React.FocusEvent<HTMLInputElement>) => {
          try {
            event.currentTarget.select()
          } catch {
            // Some input types (e.g. number) do not support text selection.
          }
          onFocus?.(event)
        }
      : onFocus

    const wrapperClassName = classNames(
      { "tds-input--auto-width": autoWidth },
      className
    )

    return (
      <InputTextBase
        className={wrapperClassName}
        description={description}
        hideLabel={hideLabel || !!ariaLabelledBy}
        id={id}
        invalid={invalid}
        label={label}
        size={size}
      >
        {({ descriptionId, inputId }) => (
          <input
            {...restProps}
            {...autoCompleteBlockingProps}
            aria-describedby={descriptionId}
            aria-invalid={invalid || undefined}
            aria-label={hideLabel ? ariaLabel || (label as string) : ariaLabel}
            aria-labelledby={ariaLabelledBy}
            autoComplete={autoComplete}
            id={inputId}
            onFocus={handleFocus}
            ref={ref}
            type={type}
          />
        )}
      </InputTextBase>
    )
  }
)

Input.displayName = "Input"
